import { useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { RelatedItem, mapUsageSafetyFromDB } from "@/types/oil";
import { Product } from "@/types/product";

interface RelatedItemFromDB {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  sort_order: number | null;
}

function toRelatedItem(item: RelatedItemFromDB): RelatedItem {
  return {
    id: item.id,
    name: item.name,
    description: item.description || undefined,
    icon: item.icon || undefined,
    sortOrder: item.sort_order ?? undefined,
  };
}

function legacyToRelatedItems(items: string[]): RelatedItem[] {
  return items.map((name, index) => ({ id: `legacy-${index}`, name }));
}

const sortByOrder = (a: RelatedItem, b: RelatedItem) =>
  (a.sortOrder ?? 999) - (b.sortOrder ?? 999);

function mapDBToProduct(dbSupply: any): Product {
  const categories = (dbSupply.supply_category_relations?.map(
    (rel: any) => toRelatedItem(rel.supply_categories)
  ) || []).sort(sortByOrder);

  const propertiesFromRelations = (dbSupply.supply_property_relations?.map(
    (rel: any) => toRelatedItem(rel.supply_properties)
  ) || []).sort(sortByOrder);

  const usesFromRelations = (dbSupply.supply_use_relations?.map(
    (rel: any) => toRelatedItem(rel.supply_uses)
  ) || []).sort(sortByOrder);

  const safetyNotesFromRelations = (dbSupply.supply_safety_note_relations?.map(
    (rel: any) => toRelatedItem(rel.supply_safety_notes)
  ) || []).sort(sortByOrder);

  return {
    id: dbSupply.supply_id,
    uuid: dbSupply.id,
    name: dbSupply.name,
    description: dbSupply.description,
    safetyLevel: dbSupply.safety_level,
    properties: propertiesFromRelations.length > 0 ? propertiesFromRelations : legacyToRelatedItems(dbSupply.properties || []),
    uses: usesFromRelations.length > 0 ? usesFromRelations : legacyToRelatedItems(dbSupply.uses || []),
    safetyNotes: safetyNotesFromRelations.length > 0 ? safetyNotesFromRelations : legacyToRelatedItems(dbSupply.safety_notes || []),
    categories,
    imageUrl: dbSupply.image_url || undefined,
    productType: "supply",
    supplyType: dbSupply.supply_type,
    unit: dbSupply.unit,
    ...mapUsageSafetyFromDB(dbSupply),
  };
}

async function fetchSuppliesData() {
  const { data, error } = await supabase
    .from("supplies" as any)
    .select(`
      id, supply_id, name, description, supply_type, unit,
      properties, uses, safety_level, safety_notes, image_url,
      can_aromatic, can_topical, can_internal,
      is_photosensitive, requires_dilution,
      safety_pregnant, safety_children, safety_babies, safety_pets,
      supply_category_relations(
        supply_categories(id, name, description, icon, sort_order)
      ),
      supply_property_relations(
        supply_properties(id, name, description, icon, sort_order)
      ),
      supply_use_relations(
        supply_uses(id, name, description, icon, sort_order)
      ),
      supply_safety_note_relations(
        supply_safety_notes(id, name, description, icon, sort_order)
      )
    `)
    .order("name", { ascending: true });

  if (error) throw error;
  return (data as any[]) || [];
}

export function useSupplies() {
  const { data: rawData = [], isLoading, error, refetch } = useQuery({
    queryKey: ["supplies"],
    queryFn: fetchSuppliesData,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const products = useMemo(() => rawData.map(mapDBToProduct), [rawData]);

  const categories = useMemo(() => {
    const all = new Set<string>();
    products.forEach(p => p.categories.forEach(c => all.add(c.name)));
    return ["Todos", ...Array.from(all).sort()];
  }, [products]);

  const searchProducts = useCallback((query: string): Product[] => {
    const lower = query.toLowerCase();
    return products.filter(p =>
      p.name.toLowerCase().includes(lower) ||
      p.description.toLowerCase().includes(lower) ||
      p.properties.some(pr => pr.name.toLowerCase().includes(lower))
    );
  }, [products]);

  const getById = useCallback((id: string) => products.find(p => p.id === id), [products]);

  return { products, categories, loading: isLoading, error, searchProducts, getById, refetch: async () => { await refetch(); } };
}
